import { ThisConnector } from '../../../../../../api/connector'
import { mapNegativeNumberToZero } from '../../../../../../graphql/schema/queries/value-reporting/map-negative-number-to-zero'
import { Query } from '../../../../../../hooks/queries'
import { QueryClient } from 'react-query'

export type ApiFacetName =
  | 'licenses-assess'
  | 'licenses-assess-used-expiration'
  | 'licenses-assess-available-expiration'
  | 'applications-licenses'
  | 'licenses-protect'
  | 'licenses-protect-used-expiration'
  | 'licenses-protect-available-expiration'
  | 'servers-licenses'

type UiFacetName =
  | 'licensesAssess'
  | 'licensesAssessExpirationUsed'
  | 'licensesAssessExpirationAvailable'
  | 'licensesApplications'
  | 'licensesProtect'
  | 'licensesProtectExpirationUsed'
  | 'licensesProtectExpirationAvailable'
  | 'licensesServers'

type AllUiFacetNames = Record<UiFacetName, true>
const allUiFacetNames: AllUiFacetNames = {
  licensesAssess: true,
  licensesAssessExpirationUsed: true,
  licensesAssessExpirationAvailable: true,
  licensesApplications: true,
  licensesProtect: true,
  licensesProtectExpirationUsed: true,
  licensesProtectExpirationAvailable: true,
  licensesServers: true
}

const apiToUiFacetNameMap: Record<ApiFacetName, UiFacetName> = {
  'licenses-assess': 'licensesAssess',
  'licenses-assess-used-expiration': 'licensesAssessExpirationUsed',
  'licenses-assess-available-expiration': 'licensesAssessExpirationAvailable',
  'applications-licenses': 'licensesApplications',
  'licenses-protect': 'licensesProtect',
  'servers-licenses': 'licensesServers',
  'licenses-protect-used-expiration': 'licensesProtectExpirationUsed',
  'licenses-protect-available-expiration': 'licensesProtectExpirationAvailable'
}

export interface VrApiFacet {
  name: ApiFacetName
  totalCount: number
  values: ApiFacetValue[]
}

interface ApiFacetValue {
  value: string
  count: number
}

export interface VrLicensesFacet {
  totalCount: number
  values: ApiFacetValue[]
}

export interface VrLicensesQueryArgs {
  organizationUuid: string
}

export interface VrLicensesResolverResponse {
  facets: Record<UiFacetName, VrLicensesFacet>
}

interface MapBreakdownValue {
  value: string
  count: number
}

interface MapBreakdownValuesProp {
  values: MapBreakdownValue[]
}

interface LicenseExpiration {
  licenses: number
  expirationDateTime: string
}

interface LicenseBreakdown {
  total: number
  unlicensed: number
  used: number
  available: number
  expirationDateAvailableLicenses: LicenseExpiration[]
  expirationDateUsedLicenses: LicenseExpiration[]
}

// Build initial facets object
// Ensuring that each possible facet name is used as a key
// and setting sensible defaults
const initialFacets = Object.keys(allUiFacetNames).reduce(
  (prev, curr: UiFacetName) => ({
    ...prev,
    [curr]: {
      totalCount: -1,
      values: []
    }
  }),
  {} as any
)

const mapFacetValue = ({ value, count }: ApiFacetValue) => ({
  value,
  count: mapNegativeNumberToZero(count)
})

const normalizeFacet = ({
  totalCount,
  values
}: {
  totalCount: number
  values: VrLicensesFacet['values']
}) => ({
  totalCount: mapNegativeNumberToZero(totalCount),
  values: values.map(mapFacetValue)
})

const normalizeFacets = (apiFacets: VrApiFacet[]) =>
  apiFacets.reduce<Record<UiFacetName, VrLicensesFacet>>(
    (prev, { name, ...rest }) => {
      if (apiToUiFacetNameMap.hasOwnProperty(name)) {
        const uiFacetName = apiToUiFacetNameMap[name]
        return {
          ...prev,
          [uiFacetName]: normalizeFacet(rest)
        }
      }
      return prev
    },
    initialFacets
  )

export interface VrLicenseData {
  assess: LicenseBreakdown
  protect: LicenseBreakdown
}

export const mapBreakdown = ({
  licenses,
  licensesExpirationAvailable,
  licensesExpirationUsed,
  entities
}: {
  licenses: MapBreakdownValuesProp & {
    totalCount: number
  }
  licensesExpirationUsed: MapBreakdownValuesProp
  licensesExpirationAvailable: MapBreakdownValuesProp
  entities: MapBreakdownValuesProp
}): LicenseBreakdown => {
  const expirationMapper = (
    data: MapBreakdownValuesProp
  ): LicenseExpiration[] =>
    data.values.map(({ value, count }: MapBreakdownValue) => ({
      licenses: count,
      expirationDateTime: value
    }))

  const counter = (data: MapBreakdownValuesProp, stringToMatch: string) => {
    const found = data.values.find((value) => value.value === stringToMatch)
    return found ? found.count : 0
  }

  return {
    total: licenses.totalCount,
    unlicensed: counter(entities, 'unlicensed'),
    used: counter(licenses, 'used'),
    available: counter(licenses, 'available'),
    expirationDateAvailableLicenses: expirationMapper(
      licensesExpirationAvailable
    ),
    expirationDateUsedLicenses: expirationMapper(licensesExpirationUsed)
  }
}

const getNormalizedResponse = (facets: VrApiFacet[]): VrLicenseData => {
  const normalizedFacets = normalizeFacets(facets)

  return {
    assess: mapBreakdown({
      licenses: normalizedFacets.licensesAssess,
      licensesExpirationAvailable:
        normalizedFacets.licensesAssessExpirationAvailable,
      licensesExpirationUsed: normalizedFacets.licensesAssessExpirationUsed,
      entities: normalizedFacets.licensesApplications
    }),
    protect: mapBreakdown({
      licenses: normalizedFacets.licensesProtect,
      licensesExpirationAvailable:
        normalizedFacets.licensesProtectExpirationAvailable,
      licensesExpirationUsed: normalizedFacets.licensesProtectExpirationUsed,
      entities: normalizedFacets.licensesServers
    })
  }
}

export async function getVrLicenses(
  this: ThisConnector,
  { organizationUuid }: VrLicensesQueryArgs
): Promise<VrLicenseData> {
  const { facets } = await this.connector.post(
    `/organizations/${organizationUuid}/application-security-summary/licenses`,
    { facets: [] }
  )

  return getNormalizedResponse(facets)
}

export function invalidateVrLicenseQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries([Query.VR_LICENSES], {
    exact: false
  })
}
